import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  Textarea,
  Switch,
  SimpleGrid,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { FiSearch, FiGlobe, FiMapPin, FiShare2, FiBarChart2 } from 'react-icons/fi';

const FiSearchIcon = FiSearch as React.ElementType;
const FiGlobeIcon = FiGlobe as React.ElementType;
const FiMapPinIcon = FiMapPin as React.ElementType;
const FiShare2Icon = FiShare2 as React.ElementType;
const FiBarChart2Icon = FiBarChart2 as React.ElementType;

export interface SEOSettings {
  // Global SEO
  siteName: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string[];
  siteUrl: string;
  
  // Open Graph
  ogImage?: string;
  ogType?: string;
  ogSiteName?: string;
  
  // Twitter Cards
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  twitterSite?: string;
  twitterCreator?: string;
  
  // Business/Local SEO
  businessName: string;
  businessType: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  phone?: string;
  email?: string;
  geoLocation?: {
    latitude: number;
    longitude: number;
  };
  serviceArea?: string[];
  priceRange?: string;
  openingHours?: {
    dayOfWeek: string;
    opens: string;
    closes: string;
  }[];
  
  // Social Media
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
  
  // Analytics & Verification
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  googleSiteVerification?: string;
  bingVerification?: string;
  
  // Sitemap & Robots
  sitemapEnabled: boolean;
  robotsEnabled: boolean;
  robotsRules?: string;
  
  // Structured Data
  enableLocalBusinessSchema: boolean;
  enableFAQSchema: boolean;
  enableServiceSchema: boolean;
  enableReviewSchema: boolean;
  
  updatedAt?: Timestamp;
  updatedBy?: string;
}

const DEFAULT_SEO_SETTINGS: SEOSettings = {
  siteName: "Glisten",
  defaultTitle: "Glisten - Eco-Friendly Mobile Detailing",
  defaultDescription: "Seamless, on-demand detailing for busy car owners. Pick a package, choose a time, and our pros will deliver a planet-safe sparkle.",
  defaultKeywords: ["mobile car detailing", "eco-friendly detailing", "car wash", "Atlanta", "Metro Atlanta"],
  siteUrl: "https://glisten.eco",
  businessName: "Glisten",
  businessType: "AutomotiveBusiness",
  serviceArea: ["Metro Atlanta", "Atlanta", "Decatur"],
  priceRange: "$$",
  sitemapEnabled: true,
  robotsEnabled: true,
  enableLocalBusinessSchema: true,
  enableFAQSchema: true,
  enableServiceSchema: true,
  enableReviewSchema: true,
  twitterCard: "summary_large_image",
  ogType: "website",
};

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SEOSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SEOSettings>(DEFAULT_SEO_SETTINGS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'seo');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          ...DEFAULT_SEO_SETTINGS,
          ...data,
          defaultKeywords: data.defaultKeywords || DEFAULT_SEO_SETTINGS.defaultKeywords,
          serviceArea: data.serviceArea || DEFAULT_SEO_SETTINGS.serviceArea,
          openingHours: data.openingHours || [],
        } as SEOSettings);
      }
    } catch (error) {
      console.error('Error loading SEO settings:', error);
      toast({
        title: 'Error loading SEO settings',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const settingsRef = doc(db, 'appSettings', 'seo');
      const updateData = {
        ...settings,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser?.uid || 'system',
      };
      
      await updateDoc(settingsRef, updateData);
      
      toast({
        title: 'SEO settings saved',
        description: 'Your SEO configuration has been updated successfully.',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('No document to update')) {
        // Document doesn't exist, create it
        try {
          const settingsRef = doc(db, 'appSettings', 'seo');
          await setDoc(settingsRef, {
            ...settings,
            updatedAt: Timestamp.now(),
            updatedBy: currentUser?.uid || 'system',
          });
          toast({
            title: 'SEO settings created',
            description: 'Your SEO configuration has been saved.',
            status: 'success',
            duration: 5000,
          });
        } catch (createError) {
          throw createError;
        }
      } else {
        throw error;
      }
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof SEOSettings>(field: K, value: SEOSettings[K]) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (field: string, subField: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: {
        ...(prev[field as keyof SEOSettings] as any),
        [subField]: value,
      },
    }));
  };

  const addServiceArea = () => {
    setSettings(prev => ({
      ...prev,
      serviceArea: [...(prev.serviceArea || []), ''],
    }));
  };

  const updateServiceArea = (index: number, value: string) => {
    setSettings(prev => ({
      ...prev,
      serviceArea: prev.serviceArea?.map((area, i) => i === index ? value : area) || [],
    }));
  };

  const removeServiceArea = (index: number) => {
    setSettings(prev => ({
      ...prev,
      serviceArea: prev.serviceArea?.filter((_, i) => i !== index) || [],
    }));
  };

  const updateOpeningHours = (index: number, field: 'opens' | 'closes', value: string) => {
    setSettings(prev => ({
      ...prev,
      openingHours: prev.openingHours?.map((hours, i) => 
        i === index ? { ...hours, [field]: value } : hours
      ) || [],
    }));
  };

  if (loading) {
    return (
      <Box p={6}>
        <Text>Loading SEO settings...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <HStack mb={6} align="center">
          <FiSearchIcon size={24} />
          <Heading size="lg">SEO Settings</Heading>
        </HStack>

        <Alert status="info">
          <AlertIcon />
          Configure your site's search engine optimization settings. Changes will be reflected on the live site.
        </Alert>

        <Accordion defaultIndex={[0]} allowMultiple>
          {/* Global SEO */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <FiGlobeIcon />
                  <Heading size="sm">Global SEO</Heading>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Site Name</FormLabel>
                  <Input
                    value={settings.siteName}
                    onChange={(e) => updateField('siteName', e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Default Title</FormLabel>
                  <Input
                    value={settings.defaultTitle}
                    onChange={(e) => updateField('defaultTitle', e.target.value)}
                    placeholder="Page title template"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Default Description</FormLabel>
                  <Textarea
                    value={settings.defaultDescription}
                    onChange={(e) => updateField('defaultDescription', e.target.value)}
                    rows={3}
                    placeholder="Meta description"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Default Keywords (comma-separated)</FormLabel>
                  <Input
                    value={settings.defaultKeywords?.join(', ') || ''}
                    onChange={(e) => updateField('defaultKeywords', e.target.value.split(',').map(k => k.trim()).filter(Boolean))}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Site URL</FormLabel>
                  <Input
                    value={settings.siteUrl}
                    onChange={(e) => updateField('siteUrl', e.target.value)}
                    placeholder="https://glisten.eco"
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Business Information */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <FiMapPinIcon />
                  <Heading size="sm">Business Information</Heading>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Business Name</FormLabel>
                  <Input
                    value={settings.businessName}
                    onChange={(e) => updateField('businessName', e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Business Type</FormLabel>
                  <Input
                    value={settings.businessType}
                    onChange={(e) => updateField('businessType', e.target.value)}
                    placeholder="AutomotiveBusiness"
                  />
                </FormControl>

                <Divider />

                <Heading size="sm">Address</Heading>
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>Street</FormLabel>
                    <Input
                      value={settings.address?.street || ''}
                      onChange={(e) => updateNestedField('address', 'street', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      value={settings.address?.city || ''}
                      onChange={(e) => updateNestedField('address', 'city', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input
                      value={settings.address?.state || ''}
                      onChange={(e) => updateNestedField('address', 'state', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>ZIP Code</FormLabel>
                    <Input
                      value={settings.address?.zipCode || ''}
                      onChange={(e) => updateNestedField('address', 'zipCode', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input
                      value={settings.address?.country || 'USA'}
                      onChange={(e) => updateNestedField('address', 'country', e.target.value)}
                    />
                  </FormControl>
                </SimpleGrid>

                <Divider />

                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    value={settings.phone || ''}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+1-555-123-4567"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={settings.email || ''}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="contact@glisten.eco"
                  />
                </FormControl>

                <Divider />

                <Heading size="sm">Service Area</Heading>
                {settings.serviceArea?.map((area, index) => (
                  <HStack key={index}>
                    <Input
                      value={area}
                      onChange={(e) => updateServiceArea(index, e.target.value)}
                      placeholder="Metro Atlanta"
                    />
                    <Button size="sm" onClick={() => removeServiceArea(index)}>Remove</Button>
                  </HStack>
                ))}
                <Button size="sm" onClick={addServiceArea}>Add Service Area</Button>

                <FormControl>
                  <FormLabel>Price Range</FormLabel>
                  <Input
                    value={settings.priceRange || ''}
                    onChange={(e) => updateField('priceRange', e.target.value)}
                    placeholder="$$"
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Social Media & Sharing */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <FiShare2Icon />
                  <Heading size="sm">Social Media & Sharing</Heading>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Open Graph Image URL</FormLabel>
                  <Input
                    value={settings.ogImage || ''}
                    onChange={(e) => updateField('ogImage', e.target.value)}
                    placeholder="https://glisten.eco/og-image.png"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Twitter Site</FormLabel>
                  <Input
                    value={settings.twitterSite || ''}
                    onChange={(e) => updateField('twitterSite', e.target.value)}
                    placeholder="@glisten"
                  />
                </FormControl>

                <Divider />

                <Heading size="sm">Social Media Links</Heading>
                <FormControl>
                  <FormLabel>Facebook</FormLabel>
                  <Input
                    value={settings.socialLinks?.facebook || ''}
                    onChange={(e) => updateNestedField('socialLinks', 'facebook', e.target.value)}
                    placeholder="https://facebook.com/glisten"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Instagram</FormLabel>
                  <Input
                    value={settings.socialLinks?.instagram || ''}
                    onChange={(e) => updateNestedField('socialLinks', 'instagram', e.target.value)}
                    placeholder="https://instagram.com/glisten"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Twitter</FormLabel>
                  <Input
                    value={settings.socialLinks?.twitter || ''}
                    onChange={(e) => updateNestedField('socialLinks', 'twitter', e.target.value)}
                    placeholder="https://twitter.com/glisten"
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Analytics & Verification */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <FiBarChart2Icon />
                  <Heading size="sm">Analytics & Verification</Heading>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Google Analytics ID</FormLabel>
                  <Input
                    value={settings.googleAnalyticsId || ''}
                    onChange={(e) => updateField('googleAnalyticsId', e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Google Tag Manager ID</FormLabel>
                  <Input
                    value={settings.googleTagManagerId || ''}
                    onChange={(e) => updateField('googleTagManagerId', e.target.value)}
                    placeholder="GTM-XXXXXXX"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Google Site Verification</FormLabel>
                  <Input
                    value={settings.googleSiteVerification || ''}
                    onChange={(e) => updateField('googleSiteVerification', e.target.value)}
                    placeholder="verification-code"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Bing Verification</FormLabel>
                  <Input
                    value={settings.bingVerification || ''}
                    onChange={(e) => updateField('bingVerification', e.target.value)}
                    placeholder="verification-code"
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Advanced Settings */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <Heading size="sm">Advanced Settings</Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack spacing={4} align="stretch">
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>Enable Sitemap</FormLabel>
                  <Switch
                    isChecked={settings.sitemapEnabled}
                    onChange={(e) => updateField('sitemapEnabled', e.target.checked)}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>Enable Robots.txt</FormLabel>
                  <Switch
                    isChecked={settings.robotsEnabled}
                    onChange={(e) => updateField('robotsEnabled', e.target.checked)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Custom Robots.txt Rules</FormLabel>
                  <Textarea
                    value={settings.robotsRules || ''}
                    onChange={(e) => updateField('robotsRules', e.target.value)}
                    rows={6}
                    placeholder="User-agent: *&#10;Allow: /&#10;Disallow: /api/"
                    fontFamily="mono"
                  />
                </FormControl>

                <Divider />

                <Heading size="sm">Structured Data</Heading>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>Local Business Schema</FormLabel>
                  <Switch
                    isChecked={settings.enableLocalBusinessSchema}
                    onChange={(e) => updateField('enableLocalBusinessSchema', e.target.checked)}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>FAQ Schema</FormLabel>
                  <Switch
                    isChecked={settings.enableFAQSchema}
                    onChange={(e) => updateField('enableFAQSchema', e.target.checked)}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>Service Schema</FormLabel>
                  <Switch
                    isChecked={settings.enableServiceSchema}
                    onChange={(e) => updateField('enableServiceSchema', e.target.checked)}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb={0}>Review Schema</FormLabel>
                  <Switch
                    isChecked={settings.enableReviewSchema}
                    onChange={(e) => updateField('enableReviewSchema', e.target.checked)}
                  />
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        <HStack justify="flex-end" mt={6}>
          <Button onClick={loadSettings} variant="outline">
            Reset
          </Button>
          <Button
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
            colorScheme="teal"
          >
            Save SEO Settings
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default SEOSettingsComponent;

